/**
 * RoleChangeLog — who granted which role, and when.
 *
 * Reads `rbac_change_log` through /api/superadmin/rbac-changes. Every row
 * arrives with a plain-English `summary` ("Ann made Ben a Pastor"), so this
 * screen stays a feed rather than a table — which is also what works on a
 * phone.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, History, Loader2, ShieldAlert, ShieldCheck, UserCog, KeyRound } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  super_admin_granted: ShieldAlert,
  super_admin_revoked: ShieldCheck,
  permission_changed: KeyRound,
};

const TONE = {
  super_admin_granted: 'bg-rose-50 text-rose-700 border-rose-200',
  super_admin_revoked: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  account_disabled: 'bg-amber-50 text-amber-700 border-amber-200',
  permission_denied: 'bg-slate-100 text-slate-500 border-slate-200',
};

const ago = (iso) => {
  if (!iso) return '';
  const secs = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  const steps = [[60, 'sec'], [60, 'min'], [24, 'hour'], [7, 'day'], [4.35, 'week'], [12, 'month']];
  let value = secs;
  let unit = 'sec';
  for (const [size, name] of steps) {
    if (value < size) { unit = name; break; }
    value = value / size;
    unit = name;
  }
  const n = Math.floor(value);
  return `${n} ${unit}${n === 1 ? '' : 's'} ago`;
};

const FILTERS = [
  { key: '', label: 'Everything' },
  { key: 'user_role_assigned', label: 'Roles given' },
  { key: 'super_admin_granted', label: 'Super admin' },
  { key: 'permission_changed', label: 'Permissions' },
  { key: 'scope_granted', label: 'Scope' },
  { key: 'permission_denied', label: 'Blocked attempts' },
];

export default function RoleChangeLog() {
  const navigate = useNavigate();
  const [action, setAction] = useState('');
  const [who, setWho] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['rbac-changes', action, who],
    queryFn: async () => {
      const qs = new URLSearchParams({ limit: '150' });
      if (action) qs.set('action', action);
      if (who) qs.set('actor_email', who);
      const r = await fetch(`${API_URL}/api/superadmin/rbac-changes?${qs}`, { headers: authHeaders() });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Failed to load');
      return r.json();
    },
    retry: false,
  });

  const items = data?.items || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="role-change-log">
      <header className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="role-log-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-bold flex items-center gap-2 truncate">
              <History className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600 shrink-0" /> Role changes
            </h1>
            <p className="text-[11px] sm:text-xs text-slate-500 truncate">
              {data?.total ?? 0} change{(data?.total ?? 0) === 1 ? '' : 's'} on record
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-3 sm:p-4 space-y-3">
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="role-log-filters">
          {FILTERS.map((f) => (
            <Button
              key={f.key || 'all'}
              size="sm"
              variant={action === f.key ? 'default' : 'outline'}
              className="h-7 text-xs whitespace-nowrap shrink-0"
              data-testid={`role-log-filter-${f.key || 'all'}`}
              onClick={() => setAction(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <Input
          value={who}
          onChange={(e) => setWho(e.target.value)}
          placeholder="Filter by who made the change (email)"
          className="h-9 text-sm"
          data-testid="role-log-actor-filter"
        />

        {isLoading && (
          <div className="py-16 text-center text-slate-400" data-testid="role-log-loading">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading the trail…
          </div>
        )}
        {error && (
          <Card><CardContent className="p-6 text-sm text-rose-600" data-testid="role-log-error">{error.message}</CardContent></Card>
        )}
        {!isLoading && !error && items.length === 0 && (
          <Card><CardContent className="p-8 text-center text-sm text-slate-400" data-testid="role-log-empty">
            No role changes match this filter yet.
          </CardContent></Card>
        )}

        <ol className="space-y-2">
          {items.map((row, i) => {
            const Icon = ICONS[row.action] || UserCog;
            return (
              <li key={`${row.timestamp}-${i}`}
                  className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 flex gap-3 transition-colors hover:border-indigo-200"
                  data-testid={`role-log-row-${i}`}>
                <div className={`h-9 w-9 shrink-0 rounded-full border flex items-center justify-center ${TONE[row.action] || 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800 leading-snug break-words">{row.summary}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <span className="text-[11px] text-slate-400">{ago(row.timestamp)}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {(row.action || '').replace(/_/g, ' ')}
                    </Badge>
                    {row.details?.via && (
                      <Badge variant="outline" className="text-[10px] text-slate-400">
                        via {String(row.details.via).replace(/_/g, ' ')}
                      </Badge>
                    )}
                  </div>
                  {Array.isArray(row.details?.added) && row.details.added.length > 0 && (
                    <p className="text-[11px] text-emerald-700 mt-1 break-words">+ {row.details.added.join(', ')}</p>
                  )}
                  {Array.isArray(row.details?.removed) && row.details.removed.length > 0 && (
                    <p className="text-[11px] text-rose-700 mt-0.5 break-words">− {row.details.removed.join(', ')}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </main>
    </div>
  );
}
